// Shared Academy UI pieces, styled to match the booking pages
// (list-booking2, view-booking2 and add-booking2).
import React from "react";
import { Box, Button, IconButton, InputBase, Paper, Typography, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { AddCircleOutlineOutlined, CloseRounded, SearchOutlined } from "@mui/icons-material";
import { tokens } from "../../../theme";
import patternUrl from "../../../assets/pattern.png";
import type { AcademyStatus } from "./types";

export const tableHeadCellSx = { fontWeight: 600, color: "text.primary" } as const;

export function useAcademyColors() {
  const theme = useTheme();
  return { colors: tokens(theme.palette.mode), isDarkMode: theme.palette.mode === "dark" };
}

/** Filled background shared by the search box and filter selects. */
function useFilterControlSx() {
  const { colors, isDarkMode } = useAcademyColors();
  return {
    height: 40, borderRadius: "10px", fontSize: "14px",
    backgroundColor: isDarkMode ? colors.primary[800] : "#F0F4F8",
    border: 0,
    color: "text.primary", transition: "box-shadow 0.2s ease",
    "&:focus-within": { boxShadow: "0 0 0 3px rgba(97,68,56,0.12)" },
  } as const;
}

/** Outer list panel, as used around the bookings table. */
export function ListPanel({ children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> }) {
  const { colors } = useAcademyColors();
  return (
    <Paper elevation={0} sx={{ backgroundColor: colors.primary[900], borderRadius: "12px", p: "20px", ...sx }}>
      {children}
    </Paper>
  );
}

/** Pill "BACK" button from the view booking page. */
export function BackButton({ onClick, label = "BACK" }: { onClick: () => void; label?: string }) {
  const { colors } = useAcademyColors();
  return (
    <Button
      onClick={onClick}
      startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
      sx={{
        minWidth: "auto", px: 2, py: 1.1, mb: 2, borderRadius: "999px",
        bgcolor: colors.primary[800], color: colors.primary[400],
        textTransform: "none", fontWeight: 600, boxShadow: "none",
        border: `1px solid ${colors.primary[400]}`,
        "&:hover": { borderColor: `${colors.primary[400]}44` },
      }}
    >
      {label}
    </Button>
  );
}

/** Search input from the bookings list header. */
export function SearchBox({ value, onChange, placeholder = "Search...", minWidth = { xs: "160px", sm: "280px" } }: {
  value: string; onChange: (value: string) => void; placeholder?: string;
  minWidth?: string | Record<string, string>;
}) {
  const controlSx = useFilterControlSx();
  return (
    <Box sx={{ ...controlSx, display: "flex", alignItems: "center", pl: 1.5, pr: 0.5 }}>
      <SearchOutlined sx={{ fontSize: "20px", color: "#614438", mr: 1 }} />
      <InputBase
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ flex: 1, fontSize: "14px", minWidth }}
      />
      {value && (
        <IconButton size="small" onClick={() => onChange("")} aria-label="Clear search">
          <CloseRounded sx={{ fontSize: "18px", color: "text.secondary" }} />
        </IconButton>
      )}
    </Box>
  );
}

/** Brown primary action button from the bookings list header. */
export function PrimaryButton({ children, onClick, startIcon = <AddCircleOutlineOutlined />, disabled }: {
  children: React.ReactNode; onClick: () => void; startIcon?: React.ReactNode; disabled?: boolean;
}) {
  return (
    <Button
      variant="contained"
      startIcon={startIcon}
      onClick={onClick}
      disabled={disabled}
      sx={{
        backgroundColor: "#614438", color: "#fff", textTransform: "none", fontWeight: 500,
        borderRadius: "8px", whiteSpace: "nowrap", "&:hover": { backgroundColor: "#aa7b67" },
      }}
    >
      {children}
    </Button>
  );
}

/** Compact select-like filter matching the search box. */
export function FilterSelect({ value, onChange, options, allLabel, width = 170 }: {
  value: string; onChange: (value: string) => void;
  options: { value: string; label: string }[]; allLabel: string; width?: number;
}) {
  const controlSx = useFilterControlSx();
  return (
    <Box
      component="select"
      value={value}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      sx={{ ...controlSx, px: 1.5, width, flexShrink: 0, cursor: "pointer", outline: "none", fontFamily: "inherit", textOverflow: "ellipsis" }}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </Box>
  );
}

const STATUS_STYLES: Record<AcademyStatus | "completed" | "in_progress" | "not_started", { bg: string; color: string; label: string }> = {
  published: { bg: "#C8E6C9", color: "#2E7D32", label: "Published" },
  draft: { bg: "#FFE0B2", color: "#E65100", label: "Draft" },
  completed: { bg: "#C5CAE9", color: "#283593", label: "Completed" },
  in_progress: { bg: "#FFE0B2", color: "#E65100", label: "In progress" },
  not_started: { bg: "#EEEEEE", color: "#616161", label: "Not started" },
};

/** Rounded status chip using the booking status palette. */
export function StatusPill({ status }: { status: keyof typeof STATUS_STYLES }) {
  const style = STATUS_STYLES[status];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", px: "12px", py: "5px", borderRadius: "999px",
        backgroundColor: style.bg, color: style.color, fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap",
      }}
    >
      {style.label}
    </Box>
  );
}

export const learnerStatus = (course: { completed: boolean; progress: number; enrolled: boolean }) =>
  course.completed ? "completed" : course.enrolled || course.progress > 0 ? "in_progress" : "not_started";

/** Card with the patterned gradient header used by the add/edit booking layout. */
export function PatternPanel({ title, badge, children, sx, bodySx }: {
  title: React.ReactNode; badge?: string; children: React.ReactNode;
  sx?: SxProps<Theme>; bodySx?: SxProps<Theme>;
}) {
  const { colors, isDarkMode } = useAcademyColors();
  const color400 = colors.primary[400] || "#724E41";
  const color10 = colors.primary[10] || "#C29363";
  const headerGradient = isDarkMode
    ? `linear-gradient(135deg, ${colors.primary[900]}F2 0%, ${colors.primary[800]}E6 100%), url(${patternUrl})`
    : `linear-gradient(135deg, ${color400}F2 0%, ${color10}E6 50%, ${color400}F2 100%), url(${patternUrl})`;
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4, overflow: "hidden", backgroundColor: isDarkMode ? colors.primary[900] : "#fff",
        border: `1px solid ${colors.primary[300]}15`, display: "flex", flexDirection: "column", ...sx,
      }}
    >
      <Box
        sx={{
          backgroundImage: headerGradient, backgroundSize: "cover", backgroundPosition: "center",
          backgroundBlendMode: "multiply", backgroundColor: isDarkMode ? colors.primary[900] : colors.primary[800],
          px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2,
        }}
      >
        {typeof title === "string" ? (
          <Typography variant="subtitle1" sx={{ color: isDarkMode ? colors.primary[700] : "#fff", fontWeight: 600, letterSpacing: "0.3px" }}>
            {title}
          </Typography>
        ) : title}
        {badge && (
          <Box
            sx={{
              px: 2, py: 0.5, borderRadius: "20px", fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap",
              border: isDarkMode ? `1px solid ${colors.primary[300]}40` : "1px solid rgba(255,255,255,0.4)",
              color: isDarkMode ? colors.primary[300] : "#fff",
              backgroundColor: isDarkMode ? "transparent" : "rgba(255,255,255,0.08)",
            }}
          >
            {badge}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 3, flexGrow: 1, ...bodySx }}>{children}</Box>
    </Paper>
  );
}

/** Small uppercase label + value row for summaries. */
export function InfoLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ py: 1.25, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, mt: 0.5 }}>{children}</Box>
    </Box>
  );
}
